import React, { useState, memo, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import {
  Box,
  Button,
  Grid,
  Drawer,
  Typography,
  TextField,
  Tooltip,
  Divider,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  useTheme,
} from '@material-ui/core'
import Alert from '@material-ui/lab/Alert'
import { makeStyles } from '@material-ui/core/styles'

import { ArrowBack } from '@material-ui/icons'
import CloseIcon from '@material-ui/icons/Close'
import AddIcon from '@material-ui/icons/Add'
import RemoveIcon from '@material-ui/icons/Remove'
import NotificationsActiveIcon from '@material-ui/icons/NotificationsActive'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'

import SimpleBar from 'simplebar-react'
import 'simplebar/dist/simplebar.min.css'

import AreaChartPrice from '~/components/Chart/AreaChart'
import useViewportSize from '~/components/useViewportSize'
import { useLiterals } from '~/components/Literals'

import actions from '~/actions'
import track from '~/track'
import api from '~/api'

const useStyles = makeStyles(() => ({
  drawerMobile: {
    width: '100%',
  },
  drawerDesktop: {
    width: 700,
  },
  bar: {
    height: '100%',
    overflow: 'auto',
  },
  title: {
    padding: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: '1px solid #E0E0E0',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center',
    padding: 16,
    gap: 16,
  },
  formControl: {
    gap: 8,
    width: '100%',
    border: 0,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  prefixInput: {
    color: '#000',
    fontSize: 15,
    fontWeight: 500,
  },
  input: {
    width: 150,
    border: 0,
    '& input': {
      textAlign: 'right',
      fontSize: 18,
      fontWeight: 400,
      '&.Mui-disabled': { color: '#000' },
      '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
        '-webkit-appearance': 'none',
      },
    },
  },
  select: {
    width: 180,
    backgroundColor: '#EEEBEB',

    '& > .MuiSelect-root': {
      padding: 8,
      display: 'flex',
      alignItems: 'center',
      fontSize: '1rem',
    },
  },
  icon: {
    fill: theme.palette.primary.main,
    marginTop: -3,
    fontSize: 28,
  },
  loadingChart: {
    position: 'absolute',
    width: '100%',
    height: 350,
    top: 0,
    backgroundColor: 'rgba(255, 255, 255, .8)',

    display: 'grid',
    placeItems: 'center',
  },
}))

function LoadingChart({ classes }) {
  const { getLiteral } = useLiterals()

  return (
    <Grid className={classes.loadingChart}>
      <Typography variant="body1" style={{ fontSize: 20 }}>
        {getLiteral('1503')}...
      </Typography>
    </Grid>
  )
}

function DrawerAlertPrice({ direction = 'right', product, open, onClose, ...chartConfig }) {
  const { color, dataChart, periodChart, loadChart } = chartConfig

  const dataChartPrices = dataChart.map(({ value }) => value)
  const lowPrice = dataChartPrices.length && Math.min(...dataChartPrices)
  const formatLowPrice = new Intl.NumberFormat('pt-br', {
    style: 'currency',
    currency: 'BRL',
  }).format(lowPrice)

  const handleFormatInitPrice = (value) => {
    return Number(value).toLocaleString('pt-BR', { style: 'decimal', minimumFractionDigits: 2 })
  }

  const [price, setPrice] = useState(handleFormatInitPrice(lowPrice))
  const [email, setEmail] = useState('')
  const [error, setError] = useState(false)

  const classes = useStyles()
  const theme = useTheme()
  const viewport = useViewportSize()
  const dispatch = useDispatch()
  const { getLiteral } = useLiterals()
  const isMobile = viewport.width < theme.gofind.desktopMaxWidth

  const validateEmail = (email) => {
    const regex = /^[\w\d\.\_\-]+@([\w-]+\.)+[\w-]+$/

    if (regex.test(email)) {
      setError(false)
      return true
    }

    setError(true)
    return false
  }

  const formartCurrency = (value) => {
    const sanitizeValue = value.replace(/\./g, '').replace(',', '.')
    return parseFloat(sanitizeValue)
  }

  const getDigitsFromValue = (value = '') => value.replace(/(-(?!\d))|[^0-9|-]/g, '') || ''

  const padDigits = (digits) => {
    const desiredLength = 3
    const actualLength = digits.length

    if (actualLength >= desiredLength) return digits

    const amountToAdd = desiredLength - actualLength
    const padding = '0'.repeat(amountToAdd)

    return padding + digits
  }

  const removeLeadingZeros = (number) => number.replace(/^0+([0-9]+)/, '$1')

  const addDecimalToNumber = (number, separator) => {
    const centsStartingPosition = number.length - 2
    const dollars = removeLeadingZeros(number.substring(0, centsStartingPosition))
    const cents = number.substring(centsStartingPosition)
    return (dollars + separator + cents).replace(/(?=(\d{3})+(\D))\B/g, '.')
  }

  const toCurrency = (value, separator = '.') => {
    const digits = getDigitsFromValue(value)
    const digitsWithPadding = padDigits(digits)

    return addDecimalToNumber(digitsWithPadding, separator)
  }

  const handleAddUnit = () => {
    const priceFormatted = formartCurrency(price)

    setPrice(
      Number(priceFormatted + 1.0).toLocaleString('pt-BR', {
        style: 'decimal',
        minimumFractionDigits: 2,
      }),
    )
  }

  const handleRemoveUnit = () => {
    const priceFormatted = formartCurrency(price)

    setPrice(
      Number(priceFormatted - 1.0).toLocaleString('pt-BR', {
        style: 'decimal',
        minimumFractionDigits: 2,
      }),
    )
  }

  const handleChangePeriod = (event) => dispatch(actions.chart.period(event.target.value))

  const handleBlurPrice = () => (!price ? setPrice('0,00') : setPrice(price))

  const handleChangePrice = (event) => {
    const valuePrice = toCurrency(event.target.value, ',')

    if (valuePrice.length >= 9) return
    setPrice(valuePrice)
  }

  const handleCreateAlert = async (product) => {
    if (!validateEmail(email)) return

    try {
      await api.postAlertPrice({
        gtin: product.gtin,
        brand: product.brand,
        price: Number(price.replace('.', '').replace(',', '.')).toFixed(2),
        email,
      })

      track('criarAlertaPrecoOnline', { category: 'produto', name: product?.name })
      dispatch(actions.snack.add({ id: Date.now(), message: getLiteral('1543') }))
    } catch (err) {
      console.error('TCL: ~ handleCreateAlert ~ err', err)
      dispatch(actions.snack.add({ id: Date.now(), message: getLiteral('1601') }))
    } finally {
      setEmail('')
    }
  }

  useEffect(() => {
    if (!loadChart) setPrice(handleFormatInitPrice(lowPrice))
  }, [periodChart, loadChart, lowPrice])

  return (
    <Drawer
      anchor={direction}
      open={open}
      onClose={onClose}
      classes={{ paper: isMobile ? classes.drawerMobile : classes.drawerDesktop }}
      PaperProps={{ 'data-cy': 'drawer-alert-price' }}
    >
      <SimpleBar className={classes.bar}>
        <Box className={classes.title}>
          <Grid style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {isMobile && (
              <Tooltip title={getLiteral('1051')} placement="bottom">
                <IconButton aria-label={getLiteral('1051')} onClick={onClose} color="primary" size="small">
                  <ArrowBack color="inherit" />
                </IconButton>
              </Tooltip>
            )}
            <Typography variant="h6">{getLiteral('2536')}</Typography>
          </Grid>

          {!isMobile && (
            <Tooltip title={getLiteral('1251')} placement="bottom">
              <IconButton size="small" aria-label={getLiteral('1251')} onClick={onClose}>
                <CloseIcon color="inherit" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <Box className={classes.content}>
          <FormControl variant="outlined" className={classes.formControl}>
            <Typography component="span">{getLiteral('2537')}</Typography>
            <Select
              className={classes.select}
              value={periodChart}
              onChange={handleChangePeriod}
              IconComponent={(props) => <ExpandMoreIcon {...props} />}
              inputProps={{
                classes: { icon: classes.icon },
              }}
            >
              <MenuItem value="last7Days">
                <Typography variant="body1" style={{ fontWeight: 500 }}>
                  {getLiteral('2503')}
                </Typography>
              </MenuItem>
              <MenuItem value="last2Weeks">
                <Typography variant="body1" style={{ fontWeight: 500 }}>
                  {getLiteral('2550')}
                </Typography>
              </MenuItem>
              <MenuItem defaultValue value="last30Days">
                <Typography variant="body1" style={{ fontWeight: 500 }}>
                  {getLiteral('1095')}
                </Typography>
              </MenuItem>
            </Select>
          </FormControl>

          {!dataChart.length && <br />}

          <Grid style={{ position: 'relative', width: '100%' }}>
            <AreaChartPrice data={dataChart} color={color} width="100%" height={350} />
            {loadChart && <LoadingChart classes={classes} />}
          </Grid>

          {!dataChart.length && <br />}

          <Grid
            style={{
              margin: '0 auto',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 16,
            }}
          >
            <Typography variant="body1" style={{ fontWeight: 700, fontSize: '1.25rem' }}>
              {getLiteral('2538')}
            </Typography>

            <Grid style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <IconButton color="primary" onClick={handleRemoveUnit} disabled={price === '0.00' || price < '0.00'}>
                <RemoveIcon color="inherit" />
              </IconButton>

              <TextField
                type="text"
                variant="outlined"
                placeholder="0,00"
                value={price}
                onChange={handleChangePrice}
                onBlur={handleBlurPrice}
                className={classes.input}
                InputProps={{
                  startAdornment: <Typography className={classes.prefixInput}>R$</Typography>,
                }}
              />

              <IconButton color="primary" onClick={handleAddUnit}>
                <AddIcon color="inherit" />
              </IconButton>
            </Grid>

            {!!lowPrice && (
              <Typography variant="body1">
                {getLiteral('2590')}: {formatLowPrice}
              </Typography>
            )}
          </Grid>

          <Divider style={{ backgroundColor: '#E0E0E0', height: 2, width: '100%', marginBlock: 8 }} />

          <TextField
            type="email"
            error={error}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            label={<strong>{getLiteral('2540')}</strong>}
            placeholder="Ex.: seuemail@email.com.br"
            variant="outlined"
            helperText={error ? getLiteral('1220') : ''}
            InputLabelProps={{
              shrink: true,
            }}
            style={{ width: '100%' }}
          />

          <Alert icon={false} style={{ backgroundColor: '#E0E0E0', color: '#000' }}>
            {getLiteral('2541')}
          </Alert>

          <Button
            disableElevation
            variant="contained"
            color="primary"
            style={{ textTransform: 'none' }}
            disabled={!email || price === '0,00' || !price}
            startIcon={<NotificationsActiveIcon />}
            onClick={() => handleCreateAlert(product)}
          >
            <Typography variant="body1">{getLiteral('2542')}</Typography>
          </Button>
        </Box>
      </SimpleBar>
    </Drawer>
  )
}

export default memo(DrawerAlertPrice)
